import React, { useState, useEffect, useMemo } from "react";
import { CHART_OVERLAY_BADGE_STYLE } from "./badgeStyles";
import PropTypes from "prop-types";
import { useThemeStore, getThemeColors } from "../../../store/themeStore";
import {
  parseIntradayPeriodicity,
  isIntradayPeriodicity,
} from "@chartLib/lib/utils";

/**
 * Calculate the next bar close time based on the last bar's timestamp
 * @param {Date} lastBarDate - Date of the last bar
 * @param {string} periodicity - Chart periodicity (e.g., "1min", "5min", "15min", "30min", "60min")
 * @returns {Date|null} - Next bar close time or null if not applicable
 */
function calculateNextBarTime(lastBarDate, periodicity) {
  if (!lastBarDate || !periodicity) return null;

  const parsed = parseIntradayPeriodicity(periodicity);
  if (!parsed) return null;

  const { value, unit } = parsed;

  const nextBarTime = new Date(lastBarDate);

  if (unit === "min") {
    nextBarTime.setMinutes(nextBarTime.getMinutes() + value);
  } else if (unit === "h") {
    nextBarTime.setHours(nextBarTime.getHours() + value);
  }

  return nextBarTime;
}

/**
 * Calculate the next bar close time based on the current clock time
 * Used as fallback when quote is delayed and bar-based timer would show "0s"
 * @param {string} periodicity - Chart periodicity (e.g., "1min", "5min", "15min", "30min", "60min")
 * @returns {Date|null} - Next bar close time or null if not applicable
 */
function calculateNextBarTimeFromClock(periodicity) {
  if (!periodicity) return null;

  const parsed = parseIntradayPeriodicity(periodicity);
  if (!parsed) return null;

  const { value, unit } = parsed;

  const now = new Date();

  if (unit === "min") {
    const minutes = now.getMinutes();
    const currentSlot = Math.floor(minutes / value) * value;
    const nextSlot = currentSlot + value;

    const nextBarTime = new Date(now);
    nextBarTime.setSeconds(0, 0);

    if (nextSlot >= 60) {
      nextBarTime.setHours(nextBarTime.getHours() + 1);
      nextBarTime.setMinutes(nextSlot - 60);
    } else {
      nextBarTime.setMinutes(nextSlot);
    }

    return nextBarTime;
  } else if (unit === "h") {
    const hours = now.getHours();
    const currentSlot = Math.floor(hours / value) * value;
    const nextSlot = currentSlot + value;

    const nextBarTime = new Date(now);
    nextBarTime.setMinutes(0, 0, 0);

    if (nextSlot >= 24) {
      nextBarTime.setDate(nextBarTime.getDate() + 1);
      nextBarTime.setHours(nextSlot - 24);
    } else {
      nextBarTime.setHours(nextSlot);
    }

    return nextBarTime;
  }

  return null;
}

/**
 * Format time remaining as MM:SS, HH:MM:SS, or Xs for up to 1 minute
 * Uses ceiling to count from 60s to 1s (never shows 0s)
 * @param {number} milliseconds - Time remaining in milliseconds
 * @returns {string} - Formatted time string
 */
function formatTimeRemaining(milliseconds) {
  if (milliseconds <= 0) return "1s";

  // Use ceiling so timer goes 60s → 59s → ... → 2s → 1s
  const totalSeconds = Math.ceil(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (num) => String(num).padStart(2, "0");

  // Up to 1 minute (60s): show as "Xs"
  if (totalSeconds <= 60) {
    return `${totalSeconds}s`;
  }

  // Less than 1 hour: show as "MM:SS"
  if (hours === 0) {
    return `${pad(minutes)}:${pad(seconds)}`;
  }

  // 1 hour or more: show as "HH:MM:SS"
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * BarCountdownTimer - Shows countdown to next bar close
 * Only works for intraday periodicities (minutes and hours)
 * Does not work in replay mode or for daily+ periodicities
 *
 * Hybrid approach:
 * - Uses last bar timestamp for synchronized countdown (matches server time)
 * - Falls back to clock-based calculation when quote is delayed (prevents "0s" stuck)
 */
const BarCountdownTimer = ({ chartData, periodicity, inline = false }) => {
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Get theme colors
  const theme = useThemeStore((state) => state.theme);
  const themeColors = getThemeColors(theme);
  const isDarkMode = themeColors.isDark;

  // Check if we're in replay mode
  const isReplayMode =
    typeof window !== "undefined" && window.__REPLAY_MODE_ACTIVE__ === true;

  // Calculate if countdown should be shown
  const shouldShow = useMemo(() => {
    // Don't show in replay mode
    if (isReplayMode) return false;

    // Don't show if no periodicity
    if (!periodicity) return false;

    // Only show for intraday periods (minutes and hours)
    return isIntradayPeriodicity(periodicity);
  }, [isReplayMode, periodicity]);

  // Get the last bar's date for synchronization
  const lastBarDate = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;
    const lastBar = chartData[chartData.length - 1];
    return lastBar?.date || null;
  }, [chartData]);

  // Update countdown every second using hybrid approach
  useEffect(() => {
    if (!shouldShow) {
      setTimeRemaining(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();

      // Primary: calculate based on last bar (synchronized with server)
      if (lastBarDate) {
        const nextBarTimeFromBar = calculateNextBarTime(
          lastBarDate,
          periodicity,
        );
        if (nextBarTimeFromBar) {
          const remaining = nextBarTimeFromBar.getTime() - now.getTime();

          // If remaining > 0, use bar-based time (synchronized)
          if (remaining > 0) {
            setTimeRemaining(remaining);
            return;
          }
        }
      }

      // Fallback: quote is delayed, use clock-based calculation
      // This prevents the timer from getting stuck at "0s"
      const nextBarTimeFromClock = calculateNextBarTimeFromClock(periodicity);
      if (nextBarTimeFromClock) {
        const remaining = nextBarTimeFromClock.getTime() - now.getTime();
        setTimeRemaining(remaining > 0 ? remaining : 0);
      } else {
        setTimeRemaining(null);
      }
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const intervalId = setInterval(updateCountdown, 1000);

    return () => clearInterval(intervalId);
  }, [shouldShow, periodicity, lastBarDate]);

  // Don't render if conditions not met
  if (!shouldShow || timeRemaining === null) {
    return null;
  }

  return (
    <div
      style={{
        ...(inline
          ? {}
          : {
              position: "absolute",
              bottom: "35px",
              left: "8px",
              zIndex: 100,
            }),
        backgroundColor: themeColors.overlayBg,
        color: "#10b981",
        ...CHART_OVERLAY_BADGE_STYLE,
        pointerEvents: "none",
        boxShadow: isDarkMode
          ? "0 4px 6px rgba(0, 0, 0, 0.3)"
          : "0 2px 4px rgba(0, 0, 0, 0.1)",
        border: isDarkMode
          ? "1px solid rgba(16, 185, 129, 0.3)"
          : "1px solid rgba(16, 185, 129, 0.5)",
      }}
    >
      <span style={{ opacity: 0.8, marginRight: "8px", fontSize: "14px" }}>
        ⏱
      </span>
      <span style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>
        {formatTimeRemaining(timeRemaining)}
      </span>
    </div>
  );
};

BarCountdownTimer.propTypes = {
  chartData: PropTypes.array,
  periodicity: PropTypes.string,
  inline: PropTypes.bool,
};

export default BarCountdownTimer;
